import React from 'react';

import { Dialog, FormField, SelectCustomizable, TextArea } from 'ui';

import FeedbackStatusModal from './FeedbackStatusModal';
import useFeedbackModal, { COMMENT_MAX_LENGTH } from './useFeedbackModal';

import './_feedback-modal.scss';

/**
 * FeedbackModal - Form where teachers and students share product feedback:
 * a category dropdown plus a free-text comment. The outcome of the submission
 * is surfaced in a follow-up status dialog.
 */

const CATEGORY_INPUT_ID = 'feedback-category';

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function FeedbackModal({ isOpen, onClose }: Readonly<FeedbackModalProps>): JSX.Element {
  const {
    cancelLabel,
    categoryLabel,
    categoryOptions,
    comment,
    commentLabel,
    commentPlaceholder,
    handleCategoryChange,
    handleClose,
    handleCommentChange,
    handleStatusClose,
    handleSubmit,
    isFormVisible,
    isSubmitDisabled,
    isSubmitting,
    selectedOption,
    status,
    statusDescription,
    statusTitle,
    submitLabel,
    subtitle,
    title,
  } = useFeedbackModal({ isOpen, onClose });

  return (
    <>
      <Dialog
        buttonTextCancel={cancelLabel}
        buttonTextSuccess={submitLabel}
        className="feedback-modal"
        hasSeparator
        isDisabledSuccess={isSubmitDisabled}
        isOpen={isOpen && isFormVisible}
        onCancel={handleClose}
        onClose={handleClose}
        onSuccess={handleSubmit}
        size="m"
        title={title}
      >
        <div className="feedback-modal__content">
          <p className="feedback-modal__subtitle">{subtitle}</p>

          <div className="feedback-modal__field">
            <SelectCustomizable
              inputId={CATEGORY_INPUT_ID}
              isSearchable={false}
              label={categoryLabel}
              onChange={handleCategoryChange}
              options={categoryOptions}
              value={selectedOption}
            />
          </div>

          <FormField className="feedback-modal__field" isDisabled={isSubmitting} label={commentLabel}>
            <TextArea maxLength={COMMENT_MAX_LENGTH} onChange={handleCommentChange} placeholder={commentPlaceholder} rows={4} value={comment} />
          </FormField>
        </div>
      </Dialog>

      <FeedbackStatusModal
        description={statusDescription}
        isOpen={isOpen && status !== null}
        onClose={handleStatusClose}
        title={statusTitle}
        variant={status === 'error' ? 'error' : 'success'}
      />
    </>
  );
}

export default FeedbackModal;
